import { BackButton } from "components/BackButton";
import { InfoTooltip } from "components/InfoTooltip";
import { SearchResultsIndicator } from "components/SearchResultsIndicator";
import { TabHeader } from "components/TabHeader";
import { useSession } from "contexts/AuthContext";
import { SearchType } from "hooks/search/types";
import { isTopMatchesSearch } from "hooks/search/utils";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { EvaluationModal } from "../evaluation/EvaluationModal";

interface BooksPageHeaderProps {
  count: number;
  total: number | null;
  isLoading: boolean;
  selectedProducts: string[] | "ALL";
  query?: string;
  searchType?: SearchType;
}

export const BooksPageHeader = ({
  count,
  total,
  isLoading,
  selectedProducts,
  query,
  searchType,
}: BooksPageHeaderProps) => {
  const { organisation } = useSession();

  const hasInactiveStatus =
    !!organisation.settings?.inactiveProductStatus?.length;

  const { t } = useTranslation(["books"]);

  const trimmedQuery = query?.trim() ?? "";
  const isTopMatchesMode = isTopMatchesSearch(searchType, query);
  const showTopMatchesFraming = isTopMatchesMode && count > 0;

  const subtitle = useMemo(() => {
    if (isLoading && count === 0) {
      return t("books:header.subtitle.loading");
    }

    if (showTopMatchesFraming) {
      return t("books:header.subtitle.topMatches", {
        count,
        query: trimmedQuery,
      });
    }

    return t("books:header.subtitle.noMatchesFor", { query: trimmedQuery });
  }, [count, isLoading, showTopMatchesFraming, trimmedQuery, t]);

  const tooltipText = useMemo(() => {
    if (showTopMatchesFraming) {
      return searchType === SearchType.SEMANTIC
        ? t("books:header.subtitle.topMatchesTooltipSemantic", { count })
        : t("books:header.subtitle.topMatchesTooltip", { count });
    }
    return hasInactiveStatus
      ? t("books:header.subtitle.inactiveBooks")
      : t("books:header.subtitle.allTitlesAvailable");
  }, [showTopMatchesFraming, searchType, count, hasInactiveStatus, t]);

  return (
    <div>
      <BackButton />
      <div className="flex justify-between items-center">
        <TabHeader
          title={t("books:header.title")}
          subtitle={
            isTopMatchesMode ? (
              <div className="flex items-center">
                {subtitle}
                <InfoTooltip
                  text={tooltipText}
                  triggerClassName="ml-2 opacity-100"
                  iconSize="small"
                  side="right"
                  testId="info-tooltip-trigger"
                />
              </div>
            ) : (
              <SearchResultsIndicator
                count={count}
                total={total}
                isLoading={isLoading}
                tooltip={tooltipText}
              />
            )
          }
        />
        <EvaluationModal selectedProducts={selectedProducts} />
      </div>
    </div>
  );
};
